import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, alpha
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getMotion } from '../services/api';

// Transform APH parliamentarian links to ParlSearch links
// (mirrors the helper in NoticesOfMotionSearch.js so the detail page
//  links the same way the result row does)
const transformAphLinks = (html) => {
  if (!html) return html;
  return html.replace(
    /href=["']https?:\/\/(?:www\.)?aph\.gov\.au\/Senators_and_Members\/Parliamentarian\?MPID=(\d+)["']\s*target=["']_blank["']/gi,
    'href="/parliamentarians/$1"'
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export default function MotionDetail() {
  const { id } = useParams();
  const [motion, setMotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // House Notice Paper green; Senate red. Motions span both chambers.
  const isSenate = motion?.chamber === 'Senate';
  const themeColor = isSenate ? '#c62828' : '#4caf50';

  useEffect(() => {
    let cancelled = false;
    const fetchMotion = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMotion(id);
        if (!cancelled) setMotion(data);
      } catch (err) {
        console.error('Error fetching motion:', err);
        if (!cancelled) setError('Failed to load motion');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMotion();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: themeColor }} />
      </Box>
    );
  }

  if (error || !motion) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Motion not found'}</Typography>
        <Button component={RouterLink} to="/motions" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Notices of Motion
        </Button>
      </Box>
    );
  }

  const movers = Array.isArray(motion.movers) ? motion.movers : [];

  return (
    <Box>
      {/* Back link */}
      <Box sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to="/motions"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Notices of Motion
        </Button>
      </Box>

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(themeColor, 0.08)} 0%, ${alpha(themeColor, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(themeColor, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: themeColor }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: themeColor, mb: 0.5 }}
            >
              {motion.title || 'Notice of Motion'}
            </Typography>

            {motion.date && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {formatDate(motion.date)}
              </Typography>
            )}

            {/* Metadata chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {motion.chamber && (
                <Chip
                  label={motion.chamber}
                  size="small"
                  sx={{ bgcolor: themeColor, color: 'white' }}
                />
              )}
              {motion.motion_type && (
                <Chip label={motion.motion_type} size="small" variant="outlined" />
              )}
              {motion.motion_subtype && (
                <Chip label={motion.motion_subtype} size="small" variant="outlined" />
              )}
              {movers.length > 0 && (
                <Chip
                  label={`Mover${movers.length > 1 ? 's' : ''}: ${movers.join(', ')}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Dates row */}
            {(motion.notice_given_date || motion.last_updated_date) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {motion.notice_given_date && (
                  <>Notice given: {motion.notice_given_date}</>
                )}
                {motion.notice_given_date && motion.last_updated_date && ' • '}
                {motion.last_updated_date && (
                  <>Last updated: {motion.last_updated_date}</>
                )}
              </Typography>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {motion.source_url && (
                <Button
                  size="small"
                  variant="outlined"
                  href={motion.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderColor: themeColor, color: themeColor }}
                >
                  View on ParlWork
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Summary (if present, distinct from full body) */}
      {motion.summary && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Summary
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {motion.summary}
          </Typography>
        </Paper>
      )}

      {/* Motion body */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: themeColor }}>
          Motion text
        </Typography>
        {motion.content_html ? (
          <Box
            sx={{
              '& p': { mb: 2, lineHeight: 1.8 },
              '& ol, & ul': { pl: 3, my: 1 },
              '& li': { mb: 0.5 },
              '& mark': { bgcolor: 'yellow', color: 'inherit' },
              lineHeight: 1.8,
              fontSize: '0.95rem'
            }}
            dangerouslySetInnerHTML={{
              __html: transformAphLinks(motion.content_html)
            }}
          />
        ) : motion.content ? (
          <Typography
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              m: 0,
              lineHeight: 1.8,
              fontSize: '0.95rem'
            }}
          >
            {motion.content}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No motion text available.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
